import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API, formatApiErrorDetail } from "@/App";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { format, addDays, parseISO } from "date-fns";
import {
  Users,
  CalendarPlus,
  Calendar,
  Building,
  FileText,
  User,
  List,
  Settings,
  Clock,
  MapPin,
  ArrowRightLeft,
  XCircle,
  Hand,
  CheckCircle,
  ShoppingBag,
  Loader2,
  CalendarOff,
  BarChart3,
  Repeat
} from "lucide-react";

const Dashboard = () => {
  const { user, isAdmin, canManage, getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const [todayShift, setTodayShift] = useState(null);
  const [upcomingShifts, setUpcomingShifts] = useState([]);
  const [pendingRequests, setPendingRequests] = useState({
    trade_requests: [],
    drop_requests: [],
    pickup_requests: []
  });
  const [loading, setLoading] = useState(true);
  const [processingId, setProcessingId] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const today = format(new Date(), "yyyy-MM-dd");
      const futureDate = format(addDays(new Date(), 30), "yyyy-MM-dd");

      const [shiftsRes, pendingRes] = await Promise.all([
        axios.get(`${API}/shifts/my?start_date=${today}&end_date=${futureDate}`, {
          headers: getAuthHeaders(),
          
        }),
        axios.get(`${API}/dashboard/pending-requests`, {
          headers: getAuthHeaders(),
          
        })
      ]);

      const shifts = shiftsRes.data;
      const todayData = shifts.find((s) => s.date === today);
      const upcoming = shifts.filter((s) => s.date > today).slice(0, 3);

      setTodayShift(todayData || null);
      setUpcomingShifts(upcoming);
      setPendingRequests(pendingRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleEmployeeRespondTrade = async (tradeId, accept) => {
    setProcessingId(tradeId);
    try {
      await axios.put(
        `${API}/trade-requests/${tradeId}/employee-respond?accept=${accept}`,
        {},
        { headers: getAuthHeaders() }
      );
      toast.success(accept ? "Trade accepted! Sent to admin for approval." : "Trade declined.");
      fetchData();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || "Failed to process trade");
    } finally {
      setProcessingId(null);
    }
  };

  const handleAdminRespondTrade = async (tradeId, accept) => {
    setProcessingId(tradeId);
    try {
      await axios.put(
        `${API}/trade-requests/${tradeId}/admin-respond?accept=${accept}`,
        {},
        { headers: getAuthHeaders() }
      );
      toast.success(`Trade ${accept ? "approved — shifts swapped" : "denied"}`);
      fetchData();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || "Failed to process trade");
    } finally {
      setProcessingId(null);
    }
  };

  const handleApproveDrop = async (dropId, accept) => {
    setProcessingId(dropId);
    try {
      await axios.put(
        `${API}/drop-requests/${dropId}?accept=${accept}`,
        {},
        { headers: getAuthHeaders() }
      );
      toast.success(`Drop request ${accept ? "approved" : "rejected"}`);
      fetchData();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || "Failed to process drop request");
    } finally {
      setProcessingId(null);
    }
  };

  const handleApprovePickup = async (pickupId) => {
    setProcessingId(pickupId);
    try {
      const res = await axios.put(
        `${API}/shift-pickup-requests/${pickupId}/approve`,
        {},
        { headers: getAuthHeaders() }
      );
      if (res.data.conflicts && res.data.conflicts.length > 0) {
        toast.warning(`Approved with conflicts: ${res.data.conflicts.map(c => c.detail).join(", ")}`, { duration: 6000 });
      } else {
        toast.success("Shift pickup approved");
      }
      fetchData();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || "Failed to approve pickup");
    } finally {
      setProcessingId(null);
    }
  };

  const handleRejectPickup = async (pickupId) => {
    setProcessingId(pickupId);
    try {
      await axios.put(
        `${API}/shift-pickup-requests/${pickupId}/reject`,
        {},
        { headers: getAuthHeaders() }
      );
      toast.success("Shift pickup rejected");
      fetchData();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || "Failed to reject pickup");
    } finally {
      setProcessingId(null);
    }
  };

  const quickActions = [
    { icon: Calendar, label: "Work Schedule", path: "/work-schedule", color: "bg-[#F0EAEB]", textColor: "text-[#6A1E2E]" },
    { icon: Building, label: "Company Schedule", path: "/company-schedule", color: "bg-[#F0EAEB]", textColor: "text-[#6A1E2E]" },
    { icon: ShoppingBag, label: "Marketplace", path: "/marketplace", color: "bg-[#F0EAEB]", textColor: "text-[#6A1E2E]" },
    { icon: CalendarOff, label: "Time Off", path: "/time-off", color: "bg-[#F0EAEB]", textColor: "text-[#6A1E2E]" },
    { icon: List, label: "Employee List", path: "/employee-list", color: "bg-[#F0EAEB]", textColor: "text-[#6A1E2E]" },
  ];

  const adminActions = [
    { icon: Users, label: "Add Employee", path: "/add-employee", color: "bg-[#6A1E2E]", textColor: "text-white" },
    { icon: CalendarPlus, label: "Add Event", path: "/add-event", color: "bg-[#E55934]", textColor: "text-white" },
    { icon: Repeat, label: "Recurring Shifts", path: "/recurring-shifts", color: "bg-[#6A1E2E]", textColor: "text-white" },
    { icon: FileText, label: "Reports", path: "/reports", color: "bg-[#6A1E2E]", textColor: "text-white" },
    { icon: BarChart3, label: "Analytics", path: "/analytics", color: "bg-[#6A1E2E]", textColor: "text-white" },
    { icon: Settings, label: "Settings", path: "/settings", color: "bg-[#6A1E2E]", textColor: "text-white" },
  ];

  const totalPendingCount = 
    pendingRequests.trade_requests.length + 
    pendingRequests.drop_requests.length + 
    pendingRequests.pickup_requests.length;

  const [showAllTrades, setShowAllTrades] = useState(false);
  const [showAllDrops, setShowAllDrops] = useState(false);
  const [showAllPickups, setShowAllPickups] = useState(false);

  const VISIBLE_LIMIT = 2;

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="dashboard-page">
      {/* Welcome Header */}
      <div className="border-b border-[#E5E5E5] pb-6">
        <h1 className="text-4xl font-bold font-heading text-[#1A1A1A] tracking-tight" data-testid="welcome-message">
          Welcome, {user?.first_name || "Employee"}
        </h1>
        <p className="text-[#666666] mt-1">
          {format(new Date(), "EEEE, MMMM d, yyyy")}
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Left Column - Schedule Info */}
        <div className="lg:col-span-4 space-y-6">
          {/* Current Schedule */}
          <Card className="border border-[#E5E5E5] shadow-none card-hover" data-testid="current-schedule-card">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-semibold uppercase tracking-wider text-[#666666]">
                Current Schedule
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="animate-pulse space-y-2">
                  <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                  <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                </div>
              ) : todayShift ? (
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-[#6A1E2E]" />
                    <span className="font-semibold text-[#1A1A1A]">{todayShift.event_name}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-[#6A1E2E]" />
                    <span className="font-mono text-[#666666]">
                      {todayShift.start_time} - {todayShift.end_time}
                    </span>
                  </div>
                  <div className="text-sm text-[#999999]">Today</div>
                </div>
              ) : (
                <p className="text-[#999999] text-sm">No shift scheduled for today</p>
              )}
            </CardContent>
          </Card>

          {/* Upcoming Work Days */}
          <Card className="border border-[#E5E5E5] shadow-none card-hover" data-testid="upcoming-shifts-card">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-semibold uppercase tracking-wider text-[#666666]">
                Upcoming Work Days
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="animate-pulse space-y-4">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="space-y-2">
                      <div className="h-4 bg-gray-200 rounded w-full"></div>
                      <div className="h-3 bg-gray-200 rounded w-2/3"></div>
                    </div>
                  ))}
                </div>
              ) : upcomingShifts.length > 0 ? (
                <div className="space-y-3">
                  {upcomingShifts.map((shift, idx) => (
                    <div
                      key={shift.id || idx}
                      className="p-3 bg-[#FAFAFA] border border-[#E5E5E5] rounded-sm"
                      data-testid={`upcoming-shift-${idx}`}
                    >
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-semibold text-[#1A1A1A] text-sm">{shift.event_name}</p>
                          <p className="font-mono text-xs text-[#666666] mt-1">
                            {shift.start_time} - {shift.end_time}
                          </p>
                        </div>
                        <span className="text-xs font-medium text-[#6A1E2E] bg-[#F0EAEB] px-2 py-1 rounded-sm">
                          {format(parseISO(shift.date), "EEE, MMM d")}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-[#999999] text-sm">No upcoming shifts scheduled</p>
              )}
            </CardContent>
          </Card>

          {/* Trade Requests for Employees (pending_employee where user is the target) */}
          {pendingRequests.trade_requests.filter(t => t.status === "pending_employee" && t.to_user_id === user?.id).length > 0 && (
            <Card className="border border-[#E5E5E5] shadow-none" data-testid="pending-trades-employee-card">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-semibold uppercase tracking-wider text-[#666666] flex items-center gap-2">
                  <ArrowRightLeft className="w-4 h-4 text-[#E55934]" />
                  Trade Requests for You
                  <span className="bg-[#E55934] text-white text-xs px-2 py-0.5 rounded-full">
                    {pendingRequests.trade_requests.filter(t => t.status === "pending_employee" && t.to_user_id === user?.id).length}
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {pendingRequests.trade_requests.filter(t => t.status === "pending_employee" && t.to_user_id === user?.id).map((trade) => (
                  <div key={trade.id} className="p-4 bg-[#FAFAFA] border border-[#E5E5E5] rounded-sm" data-testid={`employee-trade-${trade.id}`}>
                    <div className="text-sm mb-2">
                      <span className="font-semibold">{trade.from_user_name}</span>
                      <span className="text-[#666666]"> wants to trade shifts with you</span>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="p-3 bg-white border border-[#E5E5E5] rounded-sm">
                        <div className="text-[10px] font-semibold uppercase tracking-wider text-[#999999] mb-1">You Would Get</div>
                        {trade.from_shift && (
                          <div className="space-y-0.5">
                            <div className="text-xs font-medium text-[#6A1E2E]">{trade.from_shift.event_name}</div>
                            <div className="text-[11px] text-[#666666]">{trade.from_shift.date}</div>
                            <div className="text-[11px] font-mono text-[#666666]">{trade.from_shift.start_time} - {trade.from_shift.end_time}</div>
                            {trade.from_shift.positions_needed?.length > 0 && (
                              <div className="text-[10px] text-[#999999]">{trade.from_shift.positions_needed.join(", ")}</div>
                            )}
                          </div>
                        )}
                      </div>
                      <div className="p-3 bg-white border border-[#E5E5E5] rounded-sm">
                        <div className="text-[10px] font-semibold uppercase tracking-wider text-[#999999] mb-1">You Would Give Up</div>
                        {trade.to_shift && (
                          <div className="space-y-0.5">
                            <div className="text-xs font-medium text-[#6A1E2E]">{trade.to_shift.event_name}</div>
                            <div className="text-[11px] text-[#666666]">{trade.to_shift.date}</div>
                            <div className="text-[11px] font-mono text-[#666666]">{trade.to_shift.start_time} - {trade.to_shift.end_time}</div>
                            {trade.to_shift.positions_needed?.length > 0 && (
                              <div className="text-[10px] text-[#999999]">{trade.to_shift.positions_needed.join(", ")}</div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-2 mt-3">
                      <Button size="sm" onClick={() => handleEmployeeRespondTrade(trade.id, true)} disabled={processingId === trade.id} className="bg-green-600 hover:bg-green-700 text-white h-7 text-xs" data-testid={`accept-trade-${trade.id}`}>
                        {processingId === trade.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle className="w-3 h-3 mr-1" />}
                        Accept Trade
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => handleEmployeeRespondTrade(trade.id, false)} disabled={processingId === trade.id} className="border-red-300 text-red-600 hover:bg-red-50 h-7 text-xs" data-testid={`decline-trade-${trade.id}`}>
                        <XCircle className="w-3 h-3 mr-1" />
                        Decline
                      </Button>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {/* Trades initiated by user that are in progress */}
          {pendingRequests.trade_requests.filter(t => t.from_user_id === user?.id && ["pending_employee", "pending_admin"].includes(t.status)).length > 0 && (
            <Card className="border border-[#E5E5E5] shadow-none" data-testid="my-pending-trades-card">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-semibold uppercase tracking-wider text-[#666666] flex items-center gap-2">
                  <ArrowRightLeft className="w-4 h-4 text-[#666666]" />
                  Your Trade Requests (In Progress)
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {pendingRequests.trade_requests.filter(t => t.from_user_id === user?.id && ["pending_employee", "pending_admin"].includes(t.status)).map((trade) => (
                  <div key={trade.id} className="p-3 bg-[#FAFAFA] border border-[#E5E5E5] rounded-sm">
                    <div className="flex items-center justify-between">
                      <div className="text-sm">
                        <span className="text-[#666666]">Trade with </span>
                        <span className="font-semibold">{trade.to_user_name}</span>
                      </div>
                      <span className={`text-[10px] font-semibold uppercase px-2 py-0.5 rounded-full ${trade.status === "pending_employee" ? "bg-yellow-100 text-yellow-800" : "bg-blue-100 text-blue-800"}`}>
                        {trade.status === "pending_employee" ? "Awaiting Employee" : "Awaiting Admin"}
                      </span>
                    </div>
                    {trade.from_shift && (
                      <div className="text-xs text-[#666666] mt-1">
                        Your shift: {trade.from_shift.event_name} on {trade.from_shift.date}
                      </div>
                    )}
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {/* Pending Requests Section - For Admin/Supervisor */}
          {canManage && totalPendingCount > 0 && (
            <>
              {/* Trade Requests - Admin View (pending_admin) */}
              {pendingRequests.trade_requests.filter(t => t.status === "pending_admin").length > 0 && (
                <Card className="border border-[#E5E5E5] shadow-none" data-testid="pending-trades-admin-card">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm font-semibold uppercase tracking-wider text-[#666666] flex items-center gap-2">
                      <ArrowRightLeft className="w-4 h-4 text-[#6A1E2E]" />
                      Trades Awaiting Admin Approval
                      <span className="bg-[#6A1E2E] text-white text-xs px-2 py-0.5 rounded-full">
                        {pendingRequests.trade_requests.filter(t => t.status === "pending_admin").length}
                      </span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {(showAllTrades ? pendingRequests.trade_requests.filter(t => t.status === "pending_admin") : pendingRequests.trade_requests.filter(t => t.status === "pending_admin").slice(0, VISIBLE_LIMIT)).map((trade) => (
                      <div key={trade.id} className="p-4 bg-[#FAFAFA] border border-[#E5E5E5] rounded-sm" data-testid={`admin-trade-${trade.id}`}>
                        <div className="flex items-center gap-2 mb-3">
                          <CheckCircle className="w-4 h-4 text-green-600" />
                          <span className="text-xs font-medium text-green-700 bg-green-50 px-2 py-0.5 rounded-full">Both employees agreed</span>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div className="p-3 bg-white border border-[#E5E5E5] rounded-sm">
                            <div className="text-[10px] font-semibold uppercase tracking-wider text-[#999999] mb-1">Giving Up</div>
                            <div className="text-sm font-semibold text-[#1A1A1A]">{trade.from_user_name}</div>
                            {trade.from_shift && (
                              <div className="mt-1.5 space-y-0.5">
                                <div className="text-xs font-medium text-[#6A1E2E]">{trade.from_shift.event_name}</div>
                                <div className="text-[11px] text-[#666666]">{trade.from_shift.date}</div>
                                <div className="text-[11px] font-mono text-[#666666]">{trade.from_shift.start_time} - {trade.from_shift.end_time}</div>
                                {trade.from_shift.positions_needed?.length > 0 && (
                                  <div className="text-[10px] text-[#999999]">{trade.from_shift.positions_needed.join(", ")}</div>
                                )}
                              </div>
                            )}
                          </div>
                          <div className="p-3 bg-white border border-[#E5E5E5] rounded-sm">
                            <div className="text-[10px] font-semibold uppercase tracking-wider text-[#999999] mb-1">Receiving</div>
                            <div className="text-sm font-semibold text-[#1A1A1A]">{trade.to_user_name}</div>
                            {trade.to_shift && (
                              <div className="mt-1.5 space-y-0.5">
                                <div className="text-xs font-medium text-[#6A1E2E]">{trade.to_shift.event_name}</div>
                                <div className="text-[11px] text-[#666666]">{trade.to_shift.date}</div>
                                <div className="text-[11px] font-mono text-[#666666]">{trade.to_shift.start_time} - {trade.to_shift.end_time}</div>
                                {trade.to_shift.positions_needed?.length > 0 && (
                                  <div className="text-[10px] text-[#999999]">{trade.to_shift.positions_needed.join(", ")}</div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="flex gap-2 mt-3">
                          <Button size="sm" onClick={() => handleAdminRespondTrade(trade.id, true)} disabled={processingId === trade.id} className="bg-green-600 hover:bg-green-700 text-white h-7 text-xs" data-testid={`approve-trade-${trade.id}`}>
                            {processingId === trade.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle className="w-3 h-3 mr-1" />}
                            Approve Trade
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => handleAdminRespondTrade(trade.id, false)} disabled={processingId === trade.id} className="border-red-300 text-red-600 hover:bg-red-50 h-7 text-xs" data-testid={`deny-trade-${trade.id}`}>
                            <XCircle className="w-3 h-3 mr-1" />
                            Deny
                          </Button>
                        </div>
                      </div>
                    ))}
                    {pendingRequests.trade_requests.filter(t => t.status === "pending_admin").length > VISIBLE_LIMIT && (
                      <Button variant="ghost" size="sm" onClick={() => setShowAllTrades(!showAllTrades)} className="w-full text-[#6A1E2E] hover:bg-[#F0EAEB] text-xs" data-testid="view-all-trades">
                        {showAllTrades ? "Show Less" : `View All (${pendingRequests.trade_requests.filter(t => t.status === "pending_admin").length})`}
                      </Button>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Drop Requests */}
              {pendingRequests.drop_requests.length > 0 && (
                <Card className="border border-[#E5E5E5] shadow-none" data-testid="pending-drops-card">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm font-semibold uppercase tracking-wider text-[#666666] flex items-center gap-2">
                      <XCircle className="w-4 h-4 text-[#E55934]" />
                      Dropped Shift Requests
                      <span className="bg-[#E55934] text-white text-xs px-2 py-0.5 rounded-full">
                        {pendingRequests.drop_requests.length}
                      </span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {(showAllDrops ? pendingRequests.drop_requests : pendingRequests.drop_requests.slice(0, VISIBLE_LIMIT)).map((drop) => (
                      <div key={drop.id} className="p-3 bg-[#FAFAFA] border border-[#E5E5E5] rounded-sm">
                        <div className="text-sm font-semibold">{drop.employee_name}</div>
                        {drop.shift && (
                          <div className="text-xs text-[#666666] mt-1">
                            {drop.shift.event_name} on {drop.shift.date} ({drop.shift.start_time}-{drop.shift.end_time})
                          </div>
                        )}
                        <div className="text-xs text-[#999999] mt-1 italic">"{drop.reason}"</div>
                        <div className="flex gap-2 mt-2">
                          <Button
                            size="sm"
                            onClick={() => handleApproveDrop(drop.id, true)}
                            disabled={processingId === drop.id}
                            className="bg-green-600 hover:bg-green-700 text-white h-7 text-xs"
                          >
                            {processingId === drop.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle className="w-3 h-3 mr-1" />}
                            Approve
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleApproveDrop(drop.id, false)}
                            disabled={processingId === drop.id}
                            className="border-red-300 text-red-600 hover:bg-red-50 h-7 text-xs"
                          >
                            <XCircle className="w-3 h-3 mr-1" />
                            Reject
                          </Button>
                        </div>
                      </div>
                    ))}
                    {pendingRequests.drop_requests.length > VISIBLE_LIMIT && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setShowAllDrops(!showAllDrops)}
                        className="w-full text-[#6A1E2E] hover:bg-[#F0EAEB] text-xs"
                        data-testid="view-all-drops"
                      >
                        {showAllDrops ? "Show Less" : `View All (${pendingRequests.drop_requests.length})`}
                      </Button>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Pickup Requests */}
              {pendingRequests.pickup_requests.length > 0 && (
                <Card className="border border-[#E5E5E5] shadow-none" data-testid="pending-pickups-card">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm font-semibold uppercase tracking-wider text-[#666666] flex items-center gap-2">
                      <Hand className="w-4 h-4 text-[#1E4620]" />
                      Shift Pickup Requests
                      <span className="bg-[#1E4620] text-white text-xs px-2 py-0.5 rounded-full">
                        {pendingRequests.pickup_requests.length}
                      </span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {(showAllPickups ? pendingRequests.pickup_requests : pendingRequests.pickup_requests.slice(0, VISIBLE_LIMIT)).map((pickup) => (
                      <div key={pickup.shift_id} className="p-3 bg-[#FAFAFA] border border-[#E5E5E5] rounded-sm">
                        <div className="text-sm font-semibold text-[#1A1A1A]">{pickup.event_name}</div>
                        <div className="text-xs text-[#666666]">
                          {pickup.date} | {pickup.start_time}-{pickup.end_time}
                        </div>
                        <div className="mt-3">
                          <div className="text-xs font-semibold uppercase tracking-wider text-[#666666] mb-2">
                            Requesters (by seniority & hours):
                          </div>
                          <div className="space-y-2">
                            {pickup.requesters.map((req, idx) => (
                              <div
                                key={req.id}
                                className={`flex items-center justify-between p-2 rounded-sm ${
                                  idx === 0 ? "bg-green-50 border border-green-200" : "bg-white border border-[#E5E5E5]"
                                }`}
                              >
                                <div>
                                  <span className="text-sm font-medium">{req.user_name}</span>
                                  <div className="text-xs text-[#666666]">
                                    Hired: {req.date_of_hire || "N/A"} | Week hrs: {req.scheduled_hours}
                                  </div>
                                </div>
                                <div className="flex gap-1">
                                  <Button
                                    size="sm"
                                    onClick={() => handleApprovePickup(req.id)}
                                    disabled={processingId === req.id}
                                    className="bg-green-600 hover:bg-green-700 text-white h-6 text-xs px-2"
                                  >
                                    {processingId === req.id ? <Loader2 className="w-3 h-3 animate-spin" /> : "Assign"}
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => handleRejectPickup(req.id)}
                                    disabled={processingId === req.id}
                                    className="text-red-600 hover:bg-red-50 h-6 text-xs px-2"
                                  >
                                    <XCircle className="w-3 h-3" />
                                  </Button>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    ))}
                    {pendingRequests.pickup_requests.length > VISIBLE_LIMIT && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => navigate("/shift-bids")}
                        className="w-full text-[#6A1E2E] hover:bg-[#F0EAEB] text-xs"
                        data-testid="view-all-pickups"
                      >
                        View All ({pendingRequests.pickup_requests.length})
                      </Button>
                    )}
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </div>

        {/* Right Column - Quick Actions */}
        <div className="lg:col-span-8 space-y-6">
          {/* Admin Actions */}
          {canManage && (
            <div>
              <h2 className="text-sm font-semibold uppercase tracking-wider text-[#666666] mb-4">
                Admin Actions
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {adminActions.map((action) => (
                  <Button
                    key={action.path}
                    variant="ghost"
                    className={`h-auto flex-col gap-3 p-6 ${action.color} ${action.textColor} hover:opacity-90 rounded-sm border-0`}
                    onClick={() => navigate(action.path)}
                    data-testid={`action-${action.label.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    <action.icon className="w-6 h-6" />
                    <span className="text-sm font-medium">{action.label}</span>
                  </Button>
                ))}
              </div>
            </div>
          )}

          {/* Quick Actions */}
          <div>
            <h2 className="text-sm font-semibold uppercase tracking-wider text-[#666666] mb-4">
              Quick Actions
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {quickActions.map((action) => (
                <Button
                  key={action.path}
                  variant="ghost"
                  className={`h-auto flex-col gap-3 p-6 ${action.color} ${action.textColor} hover:bg-[#E2D4D6] rounded-sm border border-[#E5E5E5]`}
                  onClick={() => navigate(action.path)}
                  data-testid={`action-${action.label.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  <action.icon className="w-6 h-6" />
                  <span className="text-sm font-medium">{action.label}</span>
                </Button>
              ))}
            </div>
          </div>

          {/* System Status */}
          {canManage && (
            <Card className="border border-[#E5E5E5] shadow-none">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-semibold uppercase tracking-wider text-[#666666]">
                  Pending Approvals Summary
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div className="p-4 bg-[#FAFAFA] rounded-sm border border-[#E5E5E5]">
                    <p className="text-2xl font-bold text-[#6A1E2E]">{pendingRequests.trade_requests.length}</p>
                    <p className="text-xs text-[#666666] uppercase tracking-wide mt-1">Trade Requests</p>
                  </div>
                  <div className="p-4 bg-[#FAFAFA] rounded-sm border border-[#E5E5E5]">
                    <p className="text-2xl font-bold text-[#E55934]">{pendingRequests.drop_requests.length}</p>
                    <p className="text-xs text-[#666666] uppercase tracking-wide mt-1">Drop Requests</p>
                  </div>
                  <div className="p-4 bg-[#FAFAFA] rounded-sm border border-[#E5E5E5]">
                    <p className="text-2xl font-bold text-[#1E4620]">{pendingRequests.pickup_requests.length}</p>
                    <p className="text-xs text-[#666666] uppercase tracking-wide mt-1">Pickup Requests</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
